import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { createDuty } from '../../utils/api';
import { useContext } from 'react';
import { UserDataContext } from '../../context/UserContext';
import { toast } from 'react-hot-toast';

const StartMission = (props) => {
    const routerNavigate = useNavigate();
    const navigate = props.navigate || routerNavigate;
    const [currentTime, setCurrentTime] = useState(new Date());
    const [capturedImage, setCapturedImage] = useState(null);
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isCreatingDuty, setIsCreatingDuty] = useState(false);
    const webcamRef = useRef(null);
    const fileInputRef = useRef(null);

    const { user } = useContext(UserDataContext);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const capture = useCallback(() => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            setCapturedImage(imageSrc);
            setIsCameraActive(false);
        }
    }, [webcamRef]);

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setCapturedImage(imageUrl);
            setIsCameraActive(false);
        }
    };

    const handleCircleClick = () => {
        // Try to use webcam for live view first, but allow file input fallback
        if (!isCameraActive && !capturedImage) {
            setIsCameraActive(true);
        } else if (capturedImage) {
            setCapturedImage(null);
            setIsCameraActive(true);
        } else if (isCameraActive) {
            capture();
        }
    };

    const getISTTimeDisplay = () => {
        const now = new Date();
        const istOffset = 5.5 * 60 * 60 * 1000;
        const istDate = new Date(now.getTime() + istOffset);
        return istDate.toISOString().replace("T", " ").slice(0, 16);
    };

    const handleStartMission = async () => {
        if (!capturedImage) {
            toast.error("Please capture a selfie first!");
            return;
        }

        if (!user || !user.collectorsId) {
            toast.error("User profile not loaded. Please refresh and try again.");
            return;
        }

        setIsCreatingDuty(true);
        try {
            // Prepare duty data
            const dutyData = {
                name: user.name,
                username: user.username,
                dsid: user.dsid,
                selfie: capturedImage, // Base64 image string
                start_time: getISTTimeDisplay(), // Use IST time
                stop_time: null, // Empty when starting
                collectorsId: user.collectorsId.toString()
            };

            // Call API to create duty
            const response = await createDuty(dutyData);

            if (response.success) {
                // Store duty ID if returned
                if (response.data?.dutyId) {
                    localStorage.setItem('active_duty_id', response.data.dutyId);
                }
                toast.success("Mission started successfully!");
                setShowSuccessModal(true);
            } else {
                toast.error(response.message || 'Failed to start mission. Please try again.');
            }
        } catch (error) {
            console.error('Error starting mission:', error);
            toast.error('Failed to start mission: ' + error.message);
        } finally {
            setIsCreatingDuty(false);
        }
    };

    const confirmNavigation = () => {
        localStorage.setItem('mission_status', 'live');
        setShowSuccessModal(false);
        if (props.onMissionStarted) {
            props.onMissionStarted();
        }
    };

    return (
        <div className="h-[100dvh] bg-white flex flex-col font-sans overflow-hidden relative">

            {/* Success Modal matching Image 2 */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-10">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-[300px] overflow-hidden">
                        <div className="p-6 text-left">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Success</h3>
                            <p className="text-gray-600 text-sm">Mission started successfully</p>
                        </div>
                        <div className="flex justify-end p-4 pt-0">
                            <button
                                onClick={confirmNavigation}
                                className="text-[#00BFA5] font-bold text-base px-5 py-2 hover:bg-teal-50 rounded-lg transition">
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex flex-col flex-1 px-8 pt-8 pb-4 max-w-md mx-auto w-full overflow-hidden">

                {/* Title */}
                <h1 className="text-2xl font-bold text-gray-900 mb-4 font-sans">
                    Start Mission
                </h1>

                {/* User Info Card */}
                <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-sm mb-6">
                    <div className="space-y-1">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm font-normal">Name</span>
                            <span className="text-gray-900 text-sm font-medium">
                                {user?.name || 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm font-normal">Username</span>
                            <span className="text-gray-900 text-sm font-medium">
                                {user?.username || 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm font-normal">Employee ID</span>
                            <span className="text-gray-900 text-sm font-medium">
                                {user?.employee_id || 'N/A'}
                            </span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm font-normal">Date & Time</span>
                            <span className="text-gray-900 text-sm font-medium">{getISTTimeDisplay(currentTime)}</span>
                        </div>
                    </div>
                </div>

                {/* Webcam / Selfie Capture Area */}
                <div className="flex flex-col items-center justify-center flex-1 mb-6">
                    {/* Hidden File Input for Camera */}
                    <input
                        type="file"
                        accept="image/*"
                        capture="user"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    <div
                        onClick={handleCircleClick}
                        className="w-52 h-52 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center bg-gray-50/50 relative group cursor-pointer active:scale-95 transition-transform overflow-hidden">
                        {isCameraActive ? (
                            <Webcam
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                videoConstraints={{ facingMode: "user" }}
                                className="w-full h-full object-cover"
                            />
                        ) : capturedImage ? (
                            <img src={capturedImage} alt="Captured selfie" className="w-full h-full object-cover" />
                        ) : (
                            <div className="bg-gray-500/10 p-4 rounded-xl">
                                <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                                </svg>
                            </div>
                        )}

                        {/* Overlay button when camera is active */}
                        {isCameraActive && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/10 transition-opacity opacity-0 group-hover:opacity-100">
                                <span className="bg-white/90 px-3 py-1 rounded-full text-[10px] font-bold text-gray-900 shadow-sm uppercase tracking-wider">Tap to Capture</span>
                            </div>
                        )}
                    </div>
                    <span className="text-gray-400 text-xs mt-3 font-normal">
                        {isCameraActive ? "Tap inside the circle to capture" : capturedImage ? "Tap to retake" : "Tap to open webcam"}
                    </span>
                </div>

                <div className="mb-20 w-full">
                    <button
                        onClick={handleStartMission}
                        disabled={isCreatingDuty}
                        className="w-full bg-[#10B981] hover:bg-[#059669] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3.5 px-6 rounded-2xl shadow-lg shadow-green-500/20 transition transform active:scale-95 text-base uppercase tracking-wider"
                    >
                        {isCreatingDuty ? 'Starting Mission...' : 'Start Mission'}
                    </button>
                </div>
            </div>

            {/* Bottom Nav */}
            <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-[0_-5px_15px_rgba(0,0,0,0.02)] z-50">
                <div className="flex items-center justify-center gap-x-20 py-4 max-w-md mx-auto">
                    <button onClick={() => navigate('/collectors-home')} className="flex flex-col items-center space-y-1 text-[#00BFA5]">
                        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                        <span className="text-[11px] font-medium">Home</span>
                    </button>
                    <button onClick={() => navigate('/logs')} className="flex flex-col items-center space-y-1 text-gray-400">
                        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-[11px] font-medium">Logs</span>
                    </button>
                </div>
            </nav>
        </div>
    );
};

export default StartMission;
